/**
 * GeneralLoadingFallback - React Suspense fallback UI
 *
 * Loading state component shown during general route transitions.
 * Prevents white flash when navigating between routes by maintaining
 * consistent UI during React Suspense boundary loading.
 *
 * WHY THIS COMPONENT:
 * - Route transitions were causing flash to white screen
 * - Suspense boundary needs consistent fallback UI
 * - Maintains visual continuity during navigation
 * - Minimal UI prevents jarring transitions
 *
 * WHY hardcoded #111 background:
 * - Cannot rely on theme context during Suspense boundary
 * - Theme provider may not be initialized yet
 * - Matches app's dark theme (sports streaming aesthetic)
 * - Prevents white flash which is harsh on eyes
 *
 * WHY no fade-in animation:
 * - Animations during route transitions can cause flashing
 * - Instant display provides smoother UX
 * - Removed to eliminate visual artifacts
 *
 * USAGE:
 * Used as fallback prop in React Suspense wrapper around general routes in App.jsx:
 * <Suspense fallback={<GeneralLoadingFallback />}>
 *   <Routes>...</Routes>
 * </Suspense>
 *
 * Shows during:
 * - Lazy loading of route components
 * - Route transitions with code splitting
 * - Dynamic imports of feature modules
 *
 * @see App.jsx - Suspense boundary implementation
 * @see src/ui/AccountLoadingFallback.tsx - Account-specific variant
 * @see src/ui/Spinner.tsx - Base spinner component
 */

import styled from 'styled-components';
import Spinner from './Spinner';

/**
 * Full-height container with dark background and centered spinner
 *
 * WHY min-height 100vh:
 * - Ensures full viewport coverage during loading
 * - Prevents layout shift when content loads
 * - Better than fixed height for responsive design
 *
 * WHY padding 2rem:
 * - Prevents spinner from touching viewport edges on small screens
 * - Maintains breathing room around spinner
 * - Mobile-friendly spacing
 */
const StyledGeneralLoadingFallback = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: #111; // Hardcoded to prevent white flash
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  // Removed fade-in animation to eliminate flashing during route transitions
`;

/**
 * GeneralLoadingFallback component
 *
 * WHY function component:
 * - No props needed (Suspense fallback is stateless)
 * - Consistent with other loading components
 * - Simplicity is key for fallback UI
 */
function GeneralLoadingFallback(): JSX.Element {
  return (
    <StyledGeneralLoadingFallback>
      <Spinner />
    </StyledGeneralLoadingFallback>
  );
}

export default GeneralLoadingFallback;
